import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_SIZES } from "@/components_v2/Button/constants";
import {
  ADMIN_TABS,
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import type { AdminOriginState } from "@/domains/certifications/types";
import {
  getInitials,
  getPathShortTitle,
  getWeekNumber,
} from "@/domains/certifications/utils";
import { AttemptCardProps } from "@/features/certifications/knowledge-check-flags/types";

import "../styles.css";

const FROM_KNOWLEDGE_CHECKS: AdminOriginState = {
  fromTab: ADMIN_TABS.KNOWLEDGE_CHECK_FLAGS,
};

const AttemptCard = ({
  attempt,
  participant,
  certification,
  onReview,
}: AttemptCardProps) => {
  const { t } = useTranslation();
  const { user } = attempt;
  const name = user.fullName || user.email;
  const week = getWeekNumber(certification.modules, attempt.module.id);
  const reason = attempt.escalationReason || "UNKNOWN";

  return (
    <article className="kc-card flex flex-col">
      <header className="kc-card-header flex flex-wrap items-center justify-between gap-300">
        <div className="flex items-center gap-200">
          <BaseAvatar
            size="default"
            className="kc-card-avatar"
            label={getInitials(name)}
            imageUrl={user.profilePicture}
            aria-hidden
          />

          <div className="flex flex-col">
            <Link
              to={certificationPaths.participant(
                certificationRouteKey(certification),
                user.id,
              )}
              state={FROM_KNOWLEDGE_CHECKS}
              className="kc-card-name body-m-medium"
            >
              {name}
            </Link>

            <span className="kc-muted body-s-regular">
              {[
                participant &&
                  getPathShortTitle(
                    t,
                    certification,
                    participant.selectedTrack,
                  ),
                week === null
                  ? attempt.module.title
                  : t("certification.admin.knowledgeCheckFlags.weekCheck", {
                      week,
                    }),
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </div>

        <dl className="kc-card-stats flex flex-wrap">
          <div className="flex flex-col">
            <dt className="kc-eyebrow body-xs-regular">
              {t("certification.admin.knowledgeCheckFlags.attempt")}
            </dt>

            <dd className="kc-strong body-s-medium">{attempt.attemptNumber}</dd>
          </div>
        </dl>
      </header>

      <div className="kc-card-body flex flex-wrap items-end justify-between gap-300">
        <p className="kc-strong body-s-regular">
          {t(
            `certification.admin.knowledgeCheckFlags.escalation.${reason}.summary`,
          )}
        </p>

        <BaseButton
          size={BUTTON_SIZES.SMALL}
          ariaLabel={t(
            "certification.admin.knowledgeCheckFlags.reviewAnswersLabel",
            { name },
          )}
          onClick={() => onReview(attempt)}
        >
          {t("certification.admin.knowledgeCheckFlags.reviewAnswers")}
        </BaseButton>
      </div>
    </article>
  );
};

export default AttemptCard;
